/**
 * Session Sync Hook
 * Wires up frontend session state to the backend /api/sessions/sync endpoint.
 * Implements offline-first sync: local changes are pushed periodically,
 * and server-side updates are pulled back.
 */

import { useEffect, useRef, useCallback } from "react";
import { useCalculatorStore, Session } from "../store/calculatorStore";
import { useAuth } from "../auth/AuthProvider";

const API_BASE =
  import.meta.env.VITE_API_URL ||
  (typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? `https://api.${window.location.hostname.replace(/^www\./, "")}`
    : "http://localhost:3001");

const SYNC_INTERVAL_MS = 30_000; // 30 seconds
const LAST_SYNC_KEY = "quanta-last-sync-timestamp";

interface SyncSession {
  guid: string;
  name: string;
  color?: string;
  position?: number;
  currentInput?: string;
  isArchived?: boolean;
  createdAt: number;
  updatedAt: number;
  lastAccessedAt?: number;
}

interface SyncResponse {
  sessions: Array<{
    guid: string;
    name: string;
    color: string | null;
    position: number;
    current_input: string | null;
    is_archived: boolean;
    created_at: string;
    updated_at: string;
    last_accessed_at: string | null;
  }>;
  syncTimestamp: number;
}

function sessionToSyncPayload(session: Session): SyncSession {
  return {
    guid: session.id,
    name: session.name,
    color: session.color,
    position: session.position,
    currentInput: session.currentInput,
    isArchived: session.isArchived,
    createdAt: session.createdAt,
    updatedAt: session.updatedAt,
    lastAccessedAt: session.lastAccessedAt,
  };
}

function serverSessionToLocal(server: SyncResponse["sessions"][0]): Session {
  return {
    id: server.guid,
    name: server.name,
    color: server.color || "#6366f1",
    position: server.position,
    currentInput: server.current_input || "",
    inputHistory: [],
    inputHistoryIndex: -1,
    isArchived: server.is_archived,
    createdAt: new Date(server.created_at).getTime(),
    updatedAt: new Date(server.updated_at).getTime(),
    lastAccessedAt: server.last_accessed_at
      ? new Date(server.last_accessed_at).getTime()
      : Date.now(),
  };
}

export function useSessionSync() {
  const { getAccessToken, isAuthenticated } = useAuth();
  const syncInProgress = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const performSync = useCallback(async () => {
    if (syncInProgress.current || !isAuthenticated) return;
    syncInProgress.current = true;

    try {
      const token = await getAccessToken();
      if (!token) return;

      const state = useCalculatorStore.getState();
      const lastSync = parseInt(localStorage.getItem(LAST_SYNC_KEY) || "0", 10);

      const payload = {
        lastSyncTimestamp: lastSync,
        sessions: state.sessions.map(sessionToSyncPayload),
      };

      const res = await fetch(`${API_BASE}/api/sessions/sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // TEMPORARY: call debug endpoint to diagnose auth/server errors
        if (res.status === 401 || res.status === 500) {
          try {
            const debugRes = await fetch(`${API_BASE}/api/auth/debug`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(payload),
            });
            const debugData = await debugRes.json();
            console.error(`[SessionSync] Debug info (${res.status}):`, JSON.stringify(debugData, null, 2));
          } catch { /* ignore debug errors */ }
        }
        return;
      }

      const data: SyncResponse = await res.json();

      // Merge server sessions into local state
      if (data.sessions && data.sessions.length > 0) {
        const currentState = useCalculatorStore.getState();
        const localSessionMap = new Map(
          currentState.sessions.map((s) => [s.id, s]),
        );

        for (const serverSession of data.sessions) {
          const local = localSessionMap.get(serverSession.guid);
          const serverUpdated = new Date(serverSession.updated_at).getTime();

          if (!local) {
            // New session from server — add it
            localSessionMap.set(
              serverSession.guid,
              serverSessionToLocal(serverSession),
            );
          } else if (serverUpdated > local.updatedAt) {
            // Server is newer — merge (preserve local inputHistory)
            localSessionMap.set(serverSession.guid, {
              ...serverSessionToLocal(serverSession),
              inputHistory: local.inputHistory,
              inputHistoryIndex: local.inputHistoryIndex,
            });
          }
          // If local is newer, we already pushed our version
        }

        const mergedSessions = Array.from(localSessionMap.values()).sort(
          (a, b) => a.position - b.position,
        );

        useCalculatorStore.setState({ sessions: mergedSessions });
      }

      localStorage.setItem(LAST_SYNC_KEY, String(data.syncTimestamp));
    } catch {
      // Silently fail — offline-first: local state is always available
    } finally {
      syncInProgress.current = false;
    }
  }, [getAccessToken, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Initial sync on mount
    performSync();

    // Periodic sync
    intervalRef.current = setInterval(performSync, SYNC_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAuthenticated, performSync]);

  // Sync before unload
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleBeforeUnload = () => {
      const token = localStorage.getItem("msal.token"); // best-effort
      if (!token) return;
      const state = useCalculatorStore.getState();
      const lastSync = parseInt(localStorage.getItem(LAST_SYNC_KEY) || "0", 10);
      const payload = JSON.stringify({
        lastSyncTimestamp: lastSync,
        sessions: state.sessions.map(sessionToSyncPayload),
      });
      // Use sendBeacon for reliability on page unload
      navigator.sendBeacon(
        `${API_BASE}/api/sessions/sync`,
        new Blob([payload], { type: "application/json" }),
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isAuthenticated]);

  return { syncNow: performSync };
}
